import { useMemo, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { parseUrlState, buildSearchParams } from '../urlState'
import STOCK_FILTERS from '../data/stockFilters'
import ETF_FILTERS from '../data/etfFilters'
import useStocksSearchModel from './useStocksSearchModel'
import useEtfsSearchModel from './useEtfsSearchModel'
import { EthicalFilterSelectedAnalytics } from '@/common/analytics/events/findStocks/EthicalFilterSelectedAnalytics'
import { StockTypeSelectedAnalytics } from '@/common/analytics/events/findStocks/StockTypeSelectedAnalytics'

export function useFindStocksController() {
    const [searchParams, setSearchParams] = useSearchParams()
    const urlState = parseUrlState(searchParams)

    const dataSource = urlState.source
    // URL stores filter `name` values (short identifiers) per decision
    const positiveFilterNames = urlState.positive
    const negativeFilterNames = urlState.negative
    const page = urlState.page

    const updateSearch = useCallback(
        (nextState: { source: 'stocks' | 'etfs'; positive: string[]; negative: string[]; page?: number }) => {
            setSearchParams(buildSearchParams(nextState))
        },
        [setSearchParams],
    )

    const currentFilters = dataSource === 'stocks' ? STOCK_FILTERS : ETF_FILTERS

    // Sanitize URL-selected names: remove any filter names that are not valid for the active source
    useEffect(() => {
        const validNames = new Set(currentFilters.map((f) => f.name))
        const sanitizedPositive = positiveFilterNames.filter((n) => validNames.has(n))
        const sanitizedNegative = negativeFilterNames.filter((n) => validNames.has(n))

        const positiveDifferent =
            sanitizedPositive.length !== positiveFilterNames.length ||
            sanitizedPositive.some((v, i) => v !== positiveFilterNames[i])
        const negativeDifferent =
            sanitizedNegative.length !== negativeFilterNames.length ||
            sanitizedNegative.some((v, i) => v !== negativeFilterNames[i])

        if (positiveDifferent || negativeDifferent) {
            updateSearch({
                source: dataSource,
                positive: sanitizedPositive,
                negative: sanitizedNegative,
                page,
            })
        }
    }, [dataSource, positiveFilterNames, negativeFilterNames, page, currentFilters, setSearchParams, updateSearch])

    const positiveOptions = currentFilters
        .filter((f) => f.isPositive)
        .map((f) => ({ label: f.tag, value: f.name, description: f.description }))
    const negativeOptions = currentFilters
        .filter((f) => !f.isPositive)
        .map((f) => ({ label: f.tag, value: f.name, description: f.description }))

    const positiveTagValues = positiveFilterNames
        .map((n) => currentFilters.find((f) => f.name === n)?.tag)
        .filter(Boolean) as string[]
    const negativeTagValues = negativeFilterNames
        .map((n) => currentFilters.find((f) => f.name === n)?.tag)
        .filter(Boolean) as string[]

    const stocksModel = useStocksSearchModel(positiveTagValues, negativeTagValues, dataSource === 'stocks')
    const etfsModel = useEtfsSearchModel(
        positiveTagValues,
        negativeTagValues,
        dataSource === 'etfs',
        page,
        (p: number) => {
            updateSearch({ source: dataSource, positive: positiveFilterNames, negative: negativeFilterNames, page: p })
        },
    )

    const { items: filteredSecurities, isLoading: isLoadingStocks } = stocksModel
    const {
        items: filteredEtfs,
        isLoading: isLoadingEtfs,
        currentPage: etfsCurrentPage,
        totalPages: etfsTotalPages,
    } = etfsModel

    const result = useMemo(() => {
        const setEtfsPage = (p: number) => {
            updateSearch({ source: dataSource, positive: positiveFilterNames, negative: negativeFilterNames, page: p })
        }

        const setDataSource = (source: 'stocks' | 'etfs') => {
            StockTypeSelectedAnalytics.track({ type: source })
            updateSearch({ source, positive: [], negative: [] })
        }

        const handlePositiveFilters = (selectedFilters: string[]) => {
            updateSearch({ source: dataSource, positive: selectedFilters, negative: negativeFilterNames })
        }

        const handleNegativeFilters = (selectedFilters: string[]) => {
            updateSearch({ source: dataSource, positive: positiveFilterNames, negative: selectedFilters })
        }

        const handleFiltersClose = () => {
            EthicalFilterSelectedAnalytics.trackDebounced(
                'ethical_filter_selected',
                {
                    positive_filter: positiveFilterNames.join(', '),
                    negative_filter: negativeFilterNames.join(', '),
                },
                2000,
            )
        }
        return {
            dataSource,
            setDataSource,
            positiveOptions,
            negativeOptions,
            onPositiveChange: handlePositiveFilters,
            onNegativeChange: handleNegativeFilters,
            onFiltersClose: handleFiltersClose,
            filteredSecurities: dataSource === 'stocks' ? filteredSecurities : filteredEtfs,
            isLoading: dataSource === 'stocks' ? isLoadingStocks : isLoadingEtfs,
            etfsPagination: {
                currentPage: etfsCurrentPage,
                setPage: setEtfsPage,
                totalPages: etfsTotalPages,
            },
            positiveFilterCount: positiveFilterNames.length,
            positiveSelected: positiveFilterNames,
            negativeSelected: negativeFilterNames,
        }
    }, [
        dataSource,
        filteredSecurities,
        filteredEtfs,
        isLoadingStocks,
        isLoadingEtfs,
        etfsCurrentPage,
        etfsTotalPages,
        positiveFilterNames,
        negativeFilterNames,
        positiveOptions,
        negativeOptions,
        updateSearch,
    ])

    return result
}

export default useFindStocksController
